import { useEffect, useRef, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { BedDouble, Bell, CalendarDays, CalendarRange, ChevronDown, ChevronsLeft, ChevronsRight, CircleHelp, Home, LayoutDashboard, LogOut, MoreHorizontal, Repeat, Settings, User as UserIcon, UserRound, Users, BarChart3, HandCoins, History, IdCard, UserCog, type LucideIcon } from "lucide-react";
import { clsx } from "clsx";
import { useQuery } from "@tanstack/react-query";
import { api, resolveAssetUrl } from "@/lib/api-client";
import { usePropertyAccess, type Permission } from "@/lib/property-access";
import { useAppStore } from "@/store/app-store";
import { initials } from "@/lib/format";
import { useBackCloseable } from "@/lib/modal-stack";
import { useVisibleNotifications } from "@/features/notifications/useNotifications";
import { usePendingBookings } from "@/hooks/usePendingBookings";
import { useSocket } from "@/providers/socket-context";
import { TempleMark, Wordmark } from "@/components/ui/TempleMark";
import type { Stay } from "@/types/domain";

type NavItem = { to: string; label: string; icon: LucideIcon; adminOnly?: boolean; permission?: Permission };

const navGroups: { title: string; items: NavItem[] }[] = [
  {
    title: "Operate",
    items: [
      { to: "/", label: "Dashboard", icon: LayoutDashboard },
      { to: "/bookings", label: "Bookings", icon: CalendarDays },
      { to: "/inventory", label: "Inventory", icon: CalendarRange },
      { to: "/rooms", label: "Rooms", icon: BedDouble },
      { to: "/guests", label: "Guests", icon: Users },
    ],
  },
  { title: "Insights", items: [{ to: "/reports", label: "Reports", icon: BarChart3, permission: "REPORTS" }, { to: "/settlements", label: "Settlements", icon: HandCoins, permission: "SETTLEMENTS" }, { to: "/activity", label: "Activity log", icon: History, permission: "REPORTS" }, { to: "/police-register", label: "Police register", icon: IdCard, permission: "GUEST_IDS" }] },
  { title: "Configure", items: [{ to: "/team", label: "Team & access", icon: UserCog }, { to: "/settings", label: "Settings", icon: Settings, adminOnly: true }] },
];

const allNav = navGroups.flatMap((group) => group.items);

// Everything not on the mobile bottom nav's primary 4 tabs lives behind "More" there.
const morePaths = ["/more", "/guests", "/reports", "/settlements", "/activity", "/police-register", "/team", "/settings", "/profile", "/notifications"];

const mobileNav = [
  { to: "/", label: "Home", icon: Home, end: true },
  { to: "/bookings", label: "Bookings", icon: CalendarDays, end: false },
  { to: "/inventory", label: "Inventory", icon: CalendarRange, end: false },
  { to: "/rooms", label: "Rooms", icon: BedDouble, end: false },
  { to: "/more", label: "More", icon: MoreHorizontal, end: false },
];

const RAIL_KEY = "darshan.sidebarCollapsed";

function readRail() {
  try { return window.localStorage.getItem(RAIL_KEY) === "1"; } catch { return false; }
}

export function WorkspaceShell() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, activeOwner, activeProperty, sidebarOpen, setSidebarOpen, clearSession, exitWorkspace } = useAppStore();
  const isAdmin = user?.role === "ADMIN";
  const access = usePropertyAccess();
  const isMoreActive = morePaths.some((path) => location.pathname === path || location.pathname.startsWith(`${path}/`));
  const title = location.pathname.startsWith("/profile")
    ? "My Profile"
    : location.pathname.startsWith("/more")
    ? "More"
    : location.pathname.startsWith("/notifications")
    ? "Notifications"
    : (allNav.find((item) => (item.to === "/" ? location.pathname === "/" : location.pathname.startsWith(item.to)))?.label ?? "Dashboard");

  const [menuOpen, setMenuOpen] = useState(false);
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(readRail);
  const menuRef = useRef<HTMLDivElement>(null);
  const switcherRef = useRef<HTMLDivElement>(null);
  const notifications = useVisibleNotifications();
  const unreadCount = (notifications.data ?? []).filter((notification) => !notification.isRead).length;
  const { connected } = useSocket();
  const pending = usePendingBookings();
  const pendingCount = (pending.data ?? []).filter((booking) => !activeProperty || !booking.stayProfileId || booking.stayProfileId === activeProperty.id).length;

  useEffect(() => {
    if (!menuOpen && !switcherOpen) return;
    const closeOnOutsideClick = (event: MouseEvent) => {
      const target = event.target as Node;
      if (menuRef.current && !menuRef.current.contains(target)) setMenuOpen(false);
      if (switcherRef.current && !switcherRef.current.contains(target)) setSwitcherOpen(false);
    };
    document.addEventListener("mousedown", closeOnOutsideClick);
    return () => document.removeEventListener("mousedown", closeOnOutsideClick);
  }, [menuOpen, switcherOpen]);
  useBackCloseable(menuOpen, () => setMenuOpen(false));
  useBackCloseable(switcherOpen, () => setSwitcherOpen(false));

  const ownerScopedProperties = useQuery({
    queryKey: ["properties", "mine"],
    queryFn: () => api<Stay[]>("/stays"),
    enabled: user?.role === "OWNER",
  });
  const canChangeProperty = isAdmin || (ownerScopedProperties.data?.length ?? 0) > 1;
  const canSwitch = canChangeProperty || isAdmin;

  const toggleRail = () => {
    setCollapsed((current) => {
      try { window.localStorage.setItem(RAIL_KEY, current ? "0" : "1"); } catch { /* preference only */ }
      return !current;
    });
  };

  const changeProperty = () => {
    setSwitcherOpen(false);
    useAppStore.setState({ activeProperty: null });
    navigate("/");
  };
  const changeOwner = () => {
    setSwitcherOpen(false);
    exitWorkspace();
    navigate("/");
  };

  const badgeFor = (to: string) => (to === "/bookings" && pendingCount > 0 ? pendingCount : 0);

  return (
    <div className={clsx("app-shell", collapsed && "app-shell--rail")}>
      <aside className={clsx("sidebar", sidebarOpen && "sidebar--open")}>
        <div className="sidebar__top">
          <div className="sidebar__brand"><TempleMark size={30} simple={collapsed} /><Wordmark className="sidebar__wordmark" /></div>
          <button type="button" className="sidebar__collapse" aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"} aria-pressed={collapsed} onClick={toggleRail}>
            {collapsed ? <ChevronsRight size={18} /> : <ChevronsLeft size={18} />}
          </button>
        </div>
        <div className="sidebar__scroll">
          <nav aria-label="Main" className="sidebar__nav">
            {navGroups.map((group) => {
              const items = group.items.filter((item) => (!item.adminOnly || isAdmin) && (!item.permission || access.can(item.permission)));
              if (!items.length) return null;
              return (
                <div className="sidebar__group" key={group.title}>
                  <div className="sidebar__group-title">{group.title}</div>
                  {items.map(({ to, label, icon: Icon }) => {
                    const badge = badgeFor(to);
                    return (
                      <NavLink key={to} to={to} end={to === "/"} title={collapsed ? label : undefined} aria-label={badge ? `${label}, ${badge} pending` : undefined} onClick={() => setSidebarOpen(false)} className={({ isActive }) => clsx("sidebar__link", isActive && "active")}>
                        <Icon size={20} aria-hidden="true" />
                        <span className="sidebar__label">{label}</span>
                        {badge > 0 && <span className="sidebar__badge">{badge}</span>}
                      </NavLink>
                    );
                  })}
                </div>
              );
            })}
          </nav>
          <div className="sidebar__bottom">
            <a className="sidebar__link" href="mailto:support@darshan.in" title={collapsed ? "Help & support" : undefined}><CircleHelp size={20} aria-hidden="true" /><span className="sidebar__label">Help & support</span></a>
            <div className="user-card">
              {user?.avatarUrl ? <img className="avatar" src={resolveAssetUrl(user.avatarUrl)} alt="" /> : <span className="avatar">{initials(user?.name)}</span>}
              <span className="sidebar__label"><strong>{user?.name}</strong><small>{isAdmin ? "Administrator" : "Property owner"}</small></span>
            </div>
          </div>
        </div>
      </aside>
      {sidebarOpen && <button className="scrim" aria-label="Close menu" onClick={() => setSidebarOpen(false)} />}

      <div className="app-main">
        <header className="topbar">
          <div className="property-switcher" ref={switcherRef}>
            <button
              type="button"
              className="property-switcher__trigger"
              title={activeProperty?.name}
              aria-label={`Property: ${activeProperty?.name ?? ""}${canSwitch ? ". Change property" : ""}`}
              aria-haspopup={canSwitch ? "menu" : undefined}
              aria-expanded={canSwitch ? switcherOpen : undefined}
              disabled={!canSwitch}
              onClick={() => setSwitcherOpen((open) => !open)}
            >
              <span className="property-switcher__context">{activeOwner?.name ?? title}</span>
              <span className="property-switcher__name"><strong>{activeProperty?.name}</strong>{canSwitch && <ChevronDown size={16} aria-hidden="true" />}</span>
            </button>
            {switcherOpen && (
              <div className="menu-panel property-switcher__panel" role="menu">
                {canChangeProperty && <button type="button" role="menuitem" onClick={changeProperty}><Repeat size={16} /> Change property</button>}
                {isAdmin && <button type="button" role="menuitem" onClick={changeOwner}><UserRound size={16} /> Change owner</button>}
              </div>
            )}
          </div>

          <div className="topbar__right">
            <span className={clsx("live-pill", !connected && "live-pill--offline")} role="status">
              <i aria-hidden="true" />{connected ? "Live" : "Offline"}
            </span>
            <button type="button" className="icon-button notification-button" aria-label={unreadCount ? `Notifications, ${unreadCount} unread` : "Notifications"} onClick={() => navigate("/notifications")}>
              <Bell size={22} />
              {unreadCount > 0 && <span className="count-badge count-badge--danger">{unreadCount > 99 ? "99+" : unreadCount}</span>}
            </button>
            <div className="avatar-menu" ref={menuRef}>
              <button type="button" className="avatar-menu__trigger" aria-label="Account menu" aria-haspopup="menu" aria-expanded={menuOpen} onClick={() => setMenuOpen((open) => !open)}>
                {user?.avatarUrl ? <img className="avatar-img" src={resolveAssetUrl(user.avatarUrl)} alt="" /> : <span className="avatar avatar--small">{initials(user?.name)}</span>}
              </button>
              {menuOpen && (
                <div className="menu-panel avatar-menu__panel" role="menu">
                  <div className="avatar-menu__header">
                    <div className="avatar-menu__header-text"><strong>{user?.name}</strong><small>{user?.email}</small></div>
                  </div>
                  <button type="button" role="menuitem" onClick={() => { setMenuOpen(false); navigate("/profile"); }}><UserIcon size={16} /> My Profile</button>
                  <button type="button" role="menuitem" className="avatar-menu__signout" onClick={clearSession}><LogOut size={16} /> Sign out</button>
                </div>
              )}
            </div>
          </div>
        </header>
        <Outlet />
      </div>

      <nav className="bottom-nav" aria-label="Main">
        {mobileNav.map(({ to, label, icon: Icon, end }) => {
          const badge = badgeFor(to);
          return (
            <NavLink key={to} to={to} end={end} aria-label={badge ? `${label}, ${badge} pending` : undefined} className={({ isActive }) => ((to === "/more" ? isMoreActive : isActive) ? "active" : undefined)}>
              <span className="bottom-nav__icon"><Icon size={22} aria-hidden="true" />{badge > 0 && <span className="count-badge">{badge}</span>}</span>
              <span className="bottom-nav__label">{label}</span>
            </NavLink>
          );
        })}
      </nav>
    </div>
  );
}
